(() => {
  /* =========================================================================
   * Interactive CRT Background (three.js) — delegated hover + per-card palette
   *
   * HOW TO OVERRIDE COLORS PER PRODUCT:
   *   Add data attributes to the card element in Liquid (either `.card` or `.card__inner`):
   *     - data-crt-hue="135"        // hue degrees 0–360 -> shader-mapped tint/bg
   *     - data-crt-tint="#00D1FF"   // exact foreground tint color (hex)
   *     - data-crt-bg="#001018"     // exact background color (hex)
   *
   * Priority: tint/bg (exact) > hue (mapped) > auto dominant-hue detection
   * Colors SNAP instantly on enter; SNAP back to initial on leave.
   * Magnifier follows cursor, but only shows while hovering a product card.
   * ========================================================================= */

  // -------- boot --------
  function ready(fn) {
    if (document.readyState === 'complete' || document.readyState === 'interactive') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  ready(() => {
    if (!window.THREE) return;
    document.querySelectorAll('[data-crt-config]').forEach(initCRT);
  });

  // -------- init per section --------
  function initCRT(root) {
    try {
      if (root.__crtInit) return; // guard: only init once per section
      root.__crtInit = true;

      const canvas = ensureCanvasForRoot(root);
      if (!canvas) return;

      const ui = readUI(root);
      const cfg = mapUIToConfig(ui);

      const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
      const DPR = Math.min(window.devicePixelRatio || 1, 2);
      renderer.setPixelRatio(Math.min(DPR, 1.75));
      resizeRenderer(renderer);

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1);
      const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), null);
      scene.add(quad);

      const crtMat = buildMaterial(cfg);
      quad.material = crtMat;

      // Remember initial palette for revert
      const initialTint = crtMat.uniforms.tintColor.value.clone();
      const initialBg   = crtMat.uniforms.bgColor.value.clone();

      // Mouse tracking (RAF-coalesced)
      let mmRAF = 0, mx = 0.5, my = 0.5;
      window.addEventListener('mousemove', (e) => {
        mx = e.clientX / window.innerWidth;
        my = 1.0 - (e.clientY / window.innerHeight);
        if (mmRAF) return;
        mmRAF = requestAnimationFrame(() => {
          crtMat.uniforms.mouse.value.set(mx, my);
          mmRAF = 0;
        });
      }, { passive: true });

      // ----- Find product cards and resolve hover targets (delegated)
      const cards = findCards(cfg.selectors);
      if (!cards.length) return;

      // Build a fast lookup map: card -> image URL
      const hueCache = new Map();     // url -> {hue, score} | null
      const huePromises = new Map();  // url -> Promise
      const cardUrl = new WeakMap();  // cardEl -> url

      for (const card of cards) {
        const url = chooseUrlForCard(card);
        if (url) cardUrl.set(card, url);
      }

      // Prewarm hues: prioritize on-screen first
      const entries = cards.map(c => ({ card: c, url: cardUrl.get(c) || '' }));
      prewarmVisibleFirst(entries, hueCache, huePromises);

      // ===== Delegated hover: one listener for the grid =====
      const HOVER_SELECTOR_RUNTIME = '.card__inner, .card'; // where you place data-crt-* attributes
      const grid = document.getElementById('ProductGridContainer') || document;
      let overEl = null;
      let overToken = 0;

      grid.addEventListener('pointerover', (e) => {
  const card = e.target.closest(HOVER_SELECTOR_RUNTIME);
  if (!card || !grid.contains(card)) return;
  if (overEl === card) return;

  // NEW: ensure lens centers on the cursor immediately on enter
  crtMat.uniforms.mouse.value.set(
    e.clientX / window.innerWidth,
    1.0 - (e.clientY / window.innerHeight)
  );

  overEl = card;
  overToken++;
  handleEnter(card, overToken);
}, { passive: true });


      grid.addEventListener('pointerout', (e) => {
        // If we leave the current card entirely, run leave logic
        if (!overEl) return;
        if (e.relatedTarget && (e.relatedTarget === overEl || overEl.contains(e.relatedTarget))) return;

        const leavingCard = overEl;
        overEl = null;
        overToken++;

        handleLeave(leavingCard, overToken);
      }, { passive: true });

      function handleEnter(card, token) {
        // Magnifier up fast
        if (cfg.enableMagnifier) tweenNumber(crtMat.uniforms.hoverAmount, 1.0, 60);

        // 1) Forced per-card palette (skip detection if present)
        const forced = getForcedPaletteFromCard(card);
        if (forced) {
  if (forced.tint || forced.bg) {
    if (forced.tint) crtMat.uniforms.tintColor.value.copy(forced.tint);
    if (forced.bg)   crtMat.uniforms.bgColor.value.copy(forced.bg);
    // NOTE: when exact colors are provided, ignore hue entirely
  } else if (forced.hue != null) {
    applyHueToCRT(crtMat, forced.hue);
  }
  nudgeRgbSplit(crtMat);
  return; // skip auto detection
}


        // 2) Auto dominant hue path (instant when cached)
        const url = cardUrl.get(card) || chooseUrlForCard(card);
        if (!url) { nudgeRgbSplit(crtMat); return; } // nothing we can do
        cardUrl.set(card, url);

        const cached = hueCache.get(url);
        if (cached && cached.hue != null) {
          // SNAP to hue instantly (no tweening)
          applyHueToCRT(crtMat, cached.hue);
          nudgeRgbSplit(crtMat);
          return;
        }

        // Launch or await in-flight detection
        let p = huePromises.get(url);
        if (!p) {
          p = getDominantHue(url, hueCache, { stride: 6, minS: 0.25, minV: 0.2, maxV: 0.92 })
            .catch(() => null)
            .finally(() => huePromises.delete(url));
          huePromises.set(url, p);
        }

        p.then((res) => {
          if (overToken !== token) return; // stale
          hueCache.set(url, res || null);
          if (res && res.hue != null && res.score > 0) {
            applyHueToCRT(crtMat, res.hue); // SNAP
          }
          nudgeRgbSplit(crtMat);
        });
      }

      function handleLeave(_card, _token) {
        // Magnifier down snappy, colors snap back to initial
        if (cfg.enableMagnifier) tweenNumber(crtMat.uniforms.hoverAmount, 0.0, 70);
        crtMat.uniforms.tintColor.value.copy(initialTint);
        crtMat.uniforms.bgColor.value.copy(initialBg);
      }

      // Animate
      const clock = new THREE.Clock();
      function tick() {
        crtMat.uniforms.time.value = clock.getElapsedTime();
        renderer.render(scene, camera);
        requestAnimationFrame(tick);
      }
      tick();

      // Resize
      window.addEventListener('resize', () => {
        resizeRenderer(renderer);
        crtMat.uniforms.aspect.value = window.innerWidth / window.innerHeight;
      });
    } catch (err) {
      console.error('[interactive-crt] init failed:', err);
    }
  }

  // -------- canvas management --------
  function ensureCanvasForRoot(root) {
    // Try inside root first (old markup)
    let canvas = root.querySelector('canvas[id^="crt-bg-canvas"]');

    // If not found (likely already moved to <body>), try by computed id
    if (!canvas) {
      const suffix = root.id ? root.id.replace(/^crt-bg-root-/, '') : '';
      const expectedId = suffix ? `crt-bg-canvas-${suffix}` : null;
      if (expectedId) canvas = document.getElementById(expectedId);
    }

    // If still not found, create one with deterministic id and append to body
    if (!canvas) {
      const suffix = root.id ? root.id.replace(/^crt-bg-root-/, '') : Math.random().toString(36).slice(2);
      const id = `crt-bg-canvas-${suffix}`;
      canvas = document.createElement('canvas');
      canvas.id = id;
      document.body.appendChild(canvas);
    }

    // Ensure correct styling and stacking for the background canvas
    canvas.classList.add('crt-fixed-bg');
    if (canvas.parentElement !== document.body) {
      canvas.parentElement.removeChild(canvas);
      document.body.appendChild(canvas);
    }
    return canvas;
  }

  // -------- UI read + mapping (0–100 UI sliders) --------
  function readAttr(root, key) { return root.getAttribute(`data-${key}`); }
  function readInt(root, key, def = 0) { const v = parseInt(readAttr(root, key) || '', 10); return isNaN(v) ? def : v; }
  function readBool(root, key) { return String(readAttr(root, key)) === 'true'; }

  function readUI(root) {
    return {
      selectors: readAttr(root, 'selectors') || '.card, .product-card, .grid__item .card',
      enableMagnifier: readBool(root, 'enable-magnifier'),

      // 0–100 sliders
      mag_radius_ui: readInt(root, 'mag-radius-ui', 41),
      mag_strength_ui: readInt(root, 'mag-strength-ui', 56),

      scan_amp_ui: readInt(root, 'scan-amp-ui', 40),
      scan_speed_ui: readInt(root, 'scan-speed-ui', 10),
      noise_amt_ui: readInt(root, 'noise-amt-ui', 30),
      rgb_shift_ui: readInt(root, 'rgb-shift-ui', 22),

      dot_scale_ui: readInt(root, 'dot-scale-ui', 53),
      dot_feather_ui: readInt(root, 'dot-feather-ui', 31),
      dot_pulse_ui: readInt(root, 'dot-pulse-ui', 10),
      move_speed_ui: readInt(root, 'move-speed-ui', 19),

      curve_base_ui: readInt(root, 'curve-base-ui', 50),
      curve_aniso_x_ui: readInt(root, 'curve-aniso-x-ui', 50),
      curve_aniso_y_ui: readInt(root, 'curve-aniso-y-ui', 35),
      curve_center_x_ui: readInt(root, 'curve-center-x-ui', 100),
      curve_center_y_ui: readInt(root, 'curve-center-y-ui', 0),
      curve_angle_ui: readInt(root, 'curve-angle-ui', 100),
      curve_noise_strength_ui: readInt(root, 'curve-noise-strength-ui', 40),
      curve_noise_scale_ui: readInt(root, 'curve-noise-scale-ui', 75),

      static_amt_ui: readInt(root, 'static-amt-ui', 47),
      static_speed_ui: readInt(root, 'static-speed-ui', 20),

      // colors
      initialTint: new THREE.Color(readAttr(root, 'initial-tint') || '#9DFF6A'),
      initialBg:   new THREE.Color(readAttr(root, 'initial-bg')   || '#071007')
    };
  }

  function mapUIToConfig(ui) {
    const lerp = (a, b, t) => a + (b - a) * t;
    const t = (v) => Math.min(1, Math.max(0, v / 100));

    // Magnifier
    const magRadius   = lerp(0.06, 0.50, t(ui.mag_radius_ui));
    const magStrength = lerp(0.10, 0.90, t(ui.mag_strength_ui));

    // CRT look
    const scanAmp   = lerp(0.0, 0.6,  t(ui.scan_amp_ui));
    const scanSpeed = lerp(0.0, 0.2,  t(ui.scan_speed_ui));
    const noiseAmt  = lerp(0.0, 0.15, t(ui.noise_amt_ui));
    const rgbShift  = lerp(0.0, 0.01, t(ui.rgb_shift_ui));

    // Halftone
    const dotScale   = Math.round(lerp(60, 400, t(ui.dot_scale_ui)));
    const dotFeather = lerp(0.02, 0.30, t(ui.dot_feather_ui));
    const dotPulse   = lerp(0.05, 0.60, t(ui.dot_pulse_ui));
    const moveSpeed  = lerp(0.0, 0.10, t(ui.move_speed_ui));

    // Curvature
    const curveBase    = lerp(0.0, 0.30, t(ui.curve_base_ui));
    const curveAnisoX  = lerp(0.5, 1.5, t(ui.curve_aniso_x_ui));
    const curveAnisoY  = lerp(0.5, 1.5, t(ui.curve_aniso_y_ui));
    const curveCenterX = lerp(0.45, 0.55, t(ui.curve_center_x_ui));
    const curveCenterY = lerp(0.45, 0.55, t(ui.curve_center_y_ui));
    const curveAngle   = lerp(-0.2, 0.2, t(ui.curve_angle_ui));
    const curveNoiseStrength = lerp(0.0, 0.15, t(ui.curve_noise_strength_ui));
    const curveNoiseScale    = lerp(0.5, 3.0,  t(ui.curve_noise_scale_ui));

    // Static
    const staticAmt   = lerp(0.0, 0.12, t(ui.static_amt_ui));
    const staticSpeed = lerp(0.2, 3.0,  t(ui.static_speed_ui));

    return {
      selectors: ui.selectors,
      enableMagnifier: ui.enableMagnifier,

      scanAmp, scanSpeed, noiseAmt, rgbShift,
      zoom: 1.0, jitterX: 0.0,

      dotScale, dotFeather, dotPulse, moveSpeed,

      initialTint: ui.initialTint, initialBg: ui.initialBg,

      magRadius, magStrength,

      curveBase,
      curveAnisoX, curveAnisoY,
      curveCenterX, curveCenterY,
      curveAngle, curveNoiseStrength, curveNoiseScale,

      staticAmt, staticSpeed
    };
  }

  // -------- layout helpers --------
  function resizeRenderer(renderer) {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  }

  function findCards(selectors) {
    const all = [];
    (selectors || '').split(',').forEach((sel) => {
      const s = sel.trim();
      if (!s) return;
      document.querySelectorAll(s).forEach((el) => all.push(el));
    });
    // Prefer the *inner* card surface if present (better hover fidelity)
    return Array.from(new Set(all.map(el => el.querySelector('.card__inner') || el)));
  }

  // Robust URL resolution for images (handles srcset/lazy)
  function pickUrlFromSrcset(srcset){
    const first = (srcset || '').split(',')[0] || '';
    const url = first.trim().split(' ').shift();
    return url || '';
  }
  function urlFromImg(img){
    return (
      img?.currentSrc ||
      pickUrlFromSrcset(img?.srcset) ||
      img?.src ||
      img?.getAttribute?.('data-src') ||
      pickUrlFromSrcset(img?.getAttribute?.('data-srcset')) ||
      ''
    );
  }

  // Convert a WEBP Shopify CDN URL to JPG (best effort), preserve querystring
  function webpToJpg(url){
    try {
      const u = new URL(url, window.location.href);
      if (u.pathname.endsWith('.webp')) {
        u.pathname = u.pathname.replace(/\.webp$/i, '.jpg');
        return u.toString();
      }
    } catch(_){}
    return url;
  }

  // Find a PNG/JPG/JPEG URL from the card's <img>; if only WEBP is present, synthesize JPG
  function resolveAllowedRasterUrl(img){
    const candidates = [
      img?.currentSrc, img?.src,
      pickUrlFromSrcset(img?.srcset),
      img?.getAttribute?.('data-src'),
      pickUrlFromSrcset(img?.getAttribute?.('data-srcset'))
    ].filter(Boolean);

    const allowed = /\.(png|jpe?g)(\?|$)/i;

    for (const u of candidates) if (allowed.test(u)) return u;

    for (const u of candidates) {
      if (/\.webp(\?|$)/i.test(u)) {
        const jpg = webpToJpg(u);
        if (allowed.test(jpg)) return jpg;
      }
    }
    return '';
  }

  function chooseUrlForCard(card){
    const override = card.getAttribute('data-img');
    if (override) return override;
    const img = card.querySelector('img');
    if (!img) return '';
    const url = resolveAllowedRasterUrl(img) || urlFromImg(img);
    return url || '';
  }

  // -------- forced palette helper (per-card data attributes) --------
  function getForcedPaletteFromCard(node) {
  const host = node?.closest?.('[data-crt-tint],[data-crt-bg],[data-crt-hue]');
  if (!host) return null;

  const hueAttr  = (host.getAttribute('data-crt-hue')  || '').trim();
  const tintAttr = (host.getAttribute('data-crt-tint') || '').trim();
  const bgAttr   = (host.getAttribute('data-crt-bg')   || '').trim();

  const out = {};
  if (tintAttr) out.tint = new THREE.Color(tintAttr);
  if (bgAttr)   out.bg   = new THREE.Color(bgAttr);
  if (hueAttr && !isNaN(hueAttr)) out.hue = Number(hueAttr);

  return (out.tint || out.bg || out.hue != null) ? out : null;
}


  // -------- shader material --------
  function buildMaterial(cfg) {
    const uniforms = {
      time:       { value: 0.0 },
      aspect:     { value: window.innerWidth / window.innerHeight },

      curvature:  { value: cfg.curveBase },
      vignette:   { value: 0.35 },
      scanAmp:    { value: cfg.scanAmp },
      scanSpeed:  { value: cfg.scanSpeed },
      noiseAmt:   { value: cfg.noiseAmt },
      rgbShift:   { value: cfg.rgbShift },

      zoom:       { value: 1.0 },
      jitterX:    { value: 0.0 },

      dotScale:   { value: cfg.dotScale },
      dotFeather: { value: cfg.dotFeather },
      dotPulse:   { value: cfg.dotPulse },
      moveSpeed:  { value: cfg.moveSpeed },

      bgColor:    { value: cfg.initialBg.clone() },
      tintColor:  { value: cfg.initialTint.clone() },

      mouse:         { value: new THREE.Vector2(0.5, 0.5) },
      hoverAmount:   { value: 0.0 },
      magRadius:     { value: cfg.magRadius },
      magStrength:   { value: cfg.magStrength },

      staticAmt:     { value: cfg.staticAmt },
      staticSpeed:   { value: cfg.staticSpeed },

      curveBase:        { value: cfg.curveBase },
      curveAniso:       { value: new THREE.Vector2(cfg.curveAnisoX, cfg.curveAnisoY) },
      curveCenter:      { value: new THREE.Vector2(cfg.curveCenterX, cfg.curveCenterY) },
      curveAngle:       { value: cfg.curveAngle },
      curveNoiseStrength:{ value: cfg.curveNoiseStrength },
      curveNoiseScale:  { value: cfg.curveNoiseScale }
    };

    return new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main(){ vUv = uv; gl_Position = vec4(position, 1.0); }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;

        uniform float time, aspect;
        uniform float curvature, vignette, scanAmp, scanSpeed, noiseAmt, rgbShift;
        uniform float zoom, jitterX;
        uniform float dotScale, dotFeather, dotPulse, moveSpeed;
        uniform vec3  bgColor, tintColor;

        uniform vec2  mouse;
        uniform float hoverAmount;
        uniform float magRadius;
        uniform float magStrength;

        uniform float staticAmt, staticSpeed;

        uniform float curveBase;
        uniform vec2  curveAniso;
        uniform vec2  curveCenter;
        uniform float curveAngle;
        uniform float curveNoiseStrength;
        uniform float curveNoiseScale;

        float hash1(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
        float valueNoise(vec2 p){
          vec2 ip = floor(p), fp = fract(p);
          vec2 u = fp*fp*(3.0-2.0*fp);
          float a = hash1(ip);
          float b = hash1(ip + vec2(1.0,0.0));
          float c = hash1(ip + vec2(0.0,1.0));
          float d = hash1(ip + vec2(1.0,1.0));
          return mix(mix(a,b,u.x), mix(c,d,u.x), u.y);
        }
        float fbm(vec2 p){
          float s = 0.0, a = 0.5;
          for(int i=0;i<4;i++){ s += a * valueNoise(p); p *= 2.0; a *= 0.5; }
          return s;
        }
        vec2 rot(vec2 p, float a){ float s = sin(a), c = cos(a); return mat2(c,-s,s,c) * p; }

        vec2 crtWarp(vec2 uv){
          vec2 c = uv - curveCenter;
          c = rot(c, curveAngle);
          c.x *= aspect;
          float r2 = c.x*c.x*curveAniso.x + c.y*c.y*curveAniso.y;

          float n = fbm((uv * curveNoiseScale) + vec2(time*0.05, -time*0.037));
          float k = curveBase + (n - 0.5) * 2.0 * curveNoiseStrength;

          c *= (1.0 + k * r2);
          c.x /= aspect;
          c = rot(c, -curveAngle);
          c += curveCenter;
          return c;
        }

        vec2 applyMagnifier(vec2 uv){
          vec2 d = uv - mouse;
          float r = length(d);
          float m = smoothstep(magRadius, 0.0, r);
          m = pow(m, 1.2);
          vec2 zoomed = mouse + d * (1.0 - magStrength);
          return mix(uv, zoomed, m * hoverAmount);
        }

        vec3 halftone(vec2 uv){
          vec2 cuv = (uv - 0.5);
          cuv *= zoom; cuv += 0.5;
          cuv.x += jitterX;
          cuv += vec2(0.15*sin(time*moveSpeed*0.7), 0.12*cos(time*0.5*moveSpeed));
          cuv = applyMagnifier(cuv);

          vec2 g = cuv * dotScale;
          vec2 id = floor(g);
          vec2 f  = fract(g) - 0.5;
          float r = length(f) * 2.0;

          float phase = id.x*0.23 + id.y*0.17;
          float anim = 0.5 + 0.5 * sin(time*3.14159*moveSpeed + phase);
          float radius = clamp(dotPulse * (0.65 + 0.35*anim), 0.04, 0.95);

          float dm = length((cuv - mouse));
          float proximity = smoothstep(0.35, 0.0, dm);
          float radiusBoost = mix(1.0, 1.7, proximity * hoverAmount);
          radius *= radiusBoost;

          float dot = 1.0 - smoothstep(radius, radius + dotFeather, r);
          return mix(bgColor, tintColor, dot);
        }

        void main(){
          vec2 uv = crtWarp(vUv);

          vec2 off = vec2(rgbShift, 0.0);
          vec3 c;
          c.r = halftone(uv + off).r;
          c.g = halftone(uv      ).g;
          c.b = halftone(uv - off).b;

          float scan = sin(uv.y * 3.14159 * 720.0 + time * scanSpeed) * scanAmp;
          c *= 1.0 - scan * 0.45;

          float film = (hash1(uv*vec2(640.0,360.0) + time*60.0) - 0.5) * 2.0 * noiseAmt;
          c += film;

          float gate = smoothstep(0.1, 0.9, 0.5 + 0.5 * sin(time * staticSpeed));
          float snow = (hash1(uv*vec2(1024.0,768.0) + floor(time*60.0)) - 0.5) * 2.0;
          float line = (hash1(vec2(floor(uv.y*720.0), floor(time*30.0))) - 0.5) * 2.0;
          c += (snow * 0.6 + line * 0.4) * staticAmt * gate;

          c *= 0.996 + 0.004 * sin(time * 10.0);

          float d = distance(uv, vec2(0.5));
          c *= smoothstep(0.96, 0.42 + vignette, 0.78 - d);

          gl_FragColor = vec4(c, 1.0);
        }
      `,
      transparent: true
    });
  }

  // -------- tweens & helpers --------
  function tweenNumber(uniform, to, dur = 300, ease = (t) => t * (2 - t)) {
  const id = (uniform.__tweenId || 0) + 1;
  uniform.__tweenId = id;

  const from = Number(uniform.value);
  const t0 = performance.now();
  (function step(now) {
    if (uniform.__tweenId !== id) return; // cancelled by a newer tween
    const p = Math.min(1, (now - t0) / dur);
    const k = ease(p);
    uniform.value = from + (to - from) * k;
    if (p < 1) requestAnimationFrame(step);
  })(t0);
}


  function nudgeRgbSplit(crtMat){
    const base = crtMat.uniforms.rgbShift.value;
    crtMat.uniforms.rgbShift.value = Math.min(0.005, base + 0.0012);
    setTimeout(() => (crtMat.uniforms.rgbShift.value = base), 70);
  }

  function clamp01(v) { return Math.min(1, Math.max(0, v)); }
  function hslToRgb(h, s, l) {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (0 <= h && h < 60) [r, g, b] = [c, x, 0];
    else if (60 <= h && h < 120) [r, g, b] = [x, c, 0];
    else if (120 <= h && h < 180) [r, g, b] = [0, c, x];
    else if (180 <= h && h < 240) [r, g, b] = [0, x, c];
    else if (240 <= h && h < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];
    return { r: r + m, g: g + m, b: b + m };
  }

  function applyHueToCRT(crtMat, hueDeg) {
    const tint = hslToRgb(hueDeg, 0.80, 0.58);
    crtMat.uniforms.tintColor.value.setRGB(clamp01(tint.r), clamp01(tint.g), clamp01(tint.b));
    const bg = hslToRgb(hueDeg, 0.65, 0.10);
    crtMat.uniforms.bgColor.value.setRGB(clamp01(bg.r), clamp01(bg.g), clamp01(bg.b));
  }

  // -------- hue detection & prewarm --------
  async function getDominantHue(url, cache, opts = { stride: 4, minS: 0.25, minV: 0.2, maxV: 0.92 }) {
    if (cache.has(url)) return cache.get(url);

    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    if (img.decode) { try { await img.decode(); } catch(e){} }

    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    const cvs = document.createElement('canvas');
    const ctx = cvs.getContext('2d', { willReadFrequently: true });
    const maxSide = 256;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    cvs.width = Math.max(1, Math.floor(w * scale));
    cvs.height = Math.max(1, Math.floor(h * scale));
    ctx.drawImage(img, 0, 0, cvs.width, cvs.height);

    const { data } = ctx.getImageData(0, 0, cvs.width, cvs.height);
    const bins = new Array(36).fill(0);
    const stride = opts.stride || 4, minS = opts.minS || 0.25, minV = opts.minV || 0.2, maxV = opts.maxV || 0.92;

    for (let y = 0; y < cvs.height; y += stride) {
      for (let x = 0; x < cvs.width; x += stride) {
        const i = (y * cvs.width + x) * 4;
        const r = data[i] / 255, g = data[i + 1] / 255, b = data[i + 2] / 255, a = data[i + 3];
        if (a < 200) continue;

        const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
        let hDeg = 0, s = max === 0 ? 0 : d / max, v = max;
        if (d !== 0) {
          if (max === r) hDeg = ((g - b) / d) % 6;
          else if (max === g) hDeg = (b - r) / d + 2;
          else hDeg = (r - g) / d + 4;
          hDeg *= 60; if (hDeg < 0) hDeg += 360;
        }
        if (s < minS || v < minV || v > maxV) continue;

        const bin = Math.floor(hDeg / 10) % 36;
        const weight = s * (0.5 + 0.5 * (1 - Math.abs(v - 0.5) * 2));
        bins[bin] += weight;
      }
    }

    let maxIdx = 0; for (let i = 1; i < 36; i++) if (bins[i] > bins[maxIdx]) maxIdx = i;
    const total = bins.reduce((a, b) => a + b, 0);
    if (total <= 0.0001) { cache.set(url, null); return null; }

    const hue = maxIdx * 10;
    const result = { hue, score: bins[maxIdx] };
    cache.set(url, result);
    return result;
  }

  function prewarmVisibleFirst(entries, hueCache, huePromises){
    const vis = [];
    const hidden = [];

    entries.forEach(e => {
      const r = e.card.getBoundingClientRect();
      const onScreen = r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
      (onScreen ? vis : hidden).push(e);
    });

    const kick = (arr) => {
      arr.forEach(({ url }) => {
        if (!url || hueCache.has(url) || huePromises.has(url)) return;
        const p = getDominantHue(url, hueCache, { stride: 6, minS: 0.25, minV: 0.2, maxV: 0.92 })
          .catch(() => null)
          .finally(() => huePromises.delete(url));
        huePromises.set(url, p);
      });
    };

    // 1) Immediate prewarm for visible items
    kick(vis);

    // 2) Idle-time prewarm for the rest
    const ric = window.requestIdleCallback || ((cb)=>setTimeout(()=>cb({ timeRemaining: () => 50 }), 200));
    ric(() => kick(hidden));

    // 3) Keep prewarming as new items scroll into view
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((changes) => {
        changes.forEach(({ isIntersecting, target }) => {
          if (!isIntersecting) return;
          const url = target.__crtUrl;
          if (url && !hueCache.has(url) && !huePromises.has(url)) {
            const p = getDominantHue(url, hueCache, { stride: 6, minS: 0.25, minV: 0.2, maxV: 0.92 })
              .catch(() => null)
              .finally(() => huePromises.delete(url));
            huePromises.set(url, p);
          }
        });
      }, { rootMargin: '200px' });

      entries.forEach(({ card, url }) => { card.__crtUrl = url; io.observe(card); });
    }
  }
})();
